import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, FileText, Pencil, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { NoGroupsMessage } from '../components/NoGroupsMessage';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { ToastContainer } from '../components/ui/Toast';
import { TenantHeading } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';
import { apiClient, ServiceReportItem, TenantReportGroup } from '../lib/api-client';
import { ServiceReportEditorDialog } from '../components/service-reports/ServiceReportEditorDialog';

function pdfName(tenantCode: string, month: string): string {
  const tenant = tenantCode.toLowerCase().replace(/[^a-z0-9_-]+/g, '_');
  const label = month.replace(/\s+/g, '_').replace(/[^A-Za-z0-9_-]+/g, '');
  return `${tenant}_${label}_Service_Report.pdf`;
}

function formatWhen(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export function ServiceReports() {
  const { user, accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('service_reports', 'read');
  const { isAllowed: canEdit } = usePermission('service_reports', 'edit');
  const { isAllowed: canPublish } = usePermission('service_reports', 'publish');
  const tenantMetaMap = useTenantMetaMap(canView);
  const tenantsToQuery = selectedTenants.length > 0 ? selectedTenants : accessibleTenants;
  const tenantKey = tenantsToQuery.join(',');

  const [groups, setGroups] = useState<TenantReportGroup[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingName, setDownloadingName] = useState<string | null>(null);
  const [publishTarget, setPublishTarget] = useState<ServiceReportItem | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [editorTarget, setEditorTarget] = useState<ServiceReportItem | null>(null);
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: 'success' | 'error' | 'info' }>>([]);

  const hasNoGroups = user && (!user.groups || user.groups.length === 0);
  const reportCount = groups.reduce((sum, group) => sum + group.reports.length, 0);

  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToasts((current) => [...current, { id: `${Date.now()}-${current.length}`, message, type }]);
  };

  const loadReports = useCallback(async () => {
    if (!tenantKey) {
      setGroups([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const next = await apiClient.getServiceReports(tenantKey.split(','));
      setGroups(next);
    } catch (err: any) {
      setError(err?.message || 'Failed to load service reports');
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  }, [tenantKey]);

  useEffect(() => {
    if (!canView || hasNoGroups) return;
    void loadReports();
  }, [canView, hasNoGroups, loadReports]);

  async function download(group: TenantReportGroup, report: ServiceReportItem) {
    setDownloadingName(report.objectName);
    try {
      const blob = await apiClient.downloadServiceReport(report.objectName);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfName(group.tenantCode, report.month);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      addToast(err?.message || 'Failed to download the PDF', 'error');
    } finally {
      setDownloadingName(null);
    }
  }

  async function confirmPublish() {
    if (!publishTarget) return;
    setIsPublishing(true);
    try {
      await apiClient.publishServiceReport(publishTarget.objectName);
      setPublishTarget(null);
      addToast('Report published.', 'success');
      await loadReports();
    } catch (err: any) {
      addToast(err?.message || 'Failed to publish the report', 'error');
    } finally {
      setIsPublishing(false);
    }
  }

  if (hasNoGroups) return <NoGroupsMessage />;

  if (!canView) {
    return (
      <Card>
        <CardContent>
          You don&apos;t have permission to view Service Reports.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={FileText}
        iconColor="text-green-600 dark:text-green-400"
        iconBgColor="bg-green-100 dark:bg-green-900/30"
        description="Monthly service reports for the selected tenants"
        count={reportCount}
        countLabel={reportCount === 1 ? 'report' : 'reports'}
        countColor="text-green-600 dark:text-green-400"
      />

      {error && (
        <Card>
          <CardContent className="text-red-600 dark:text-red-400">{error}</CardContent>
        </Card>
      )}

      {isLoading ? (
        <Skeleton className="h-32 w-full" variant="card" />
      ) : !tenantKey ? (
        <Card>
          <CardContent>Select a tenant to see service reports.</CardContent>
        </Card>
      ) : groups.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">No service reports yet.</div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const meta = resolveTenantMeta(tenantMetaMap, group.tenantCode);
            const isCollapsed = collapsed[group.tenantCode] === true;
            return (
              <Card key={group.tenantCode}>
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                  onClick={() => setCollapsed((current) => ({ ...current, [group.tenantCode]: !isCollapsed }))}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isCollapsed
                      ? <ChevronRight className="w-4 h-4 text-gray-500" />
                      : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    <TenantHeading tenant={meta} title={meta.name} subtitle={group.tenantCode} />
                  </div>
                  <Badge variant="outline">{group.reports.length}</Badge>
                </button>
                {!isCollapsed && (
                  <CardContent className="pt-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                            <th className="py-2 pr-3 font-medium">Month</th>
                            <th className="py-2 pr-3 font-medium">Status</th>
                            <th className="py-2 pr-3 font-medium">Updated</th>
                            <th className="py-2 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.reports.map((report) => (
                            <tr key={report.objectName} className="border-b border-gray-100 dark:border-gray-800">
                              <td className="py-3 pr-3 text-gray-900 dark:text-gray-100">{report.month}</td>
                              <td className="py-3 pr-3">
                                <Badge variant={report.status === 'published' ? 'success' : 'warning'}>
                                  {report.status === 'published' ? 'Published' : 'Draft'}
                                </Badge>
                              </td>
                              <td className="py-3 pr-3 text-gray-500 dark:text-gray-400">{formatWhen(report.updatedAt)}</td>
                              <td className="py-3">
                                <div className="flex flex-wrap gap-2">
                                  {report.status === 'draft' && canEdit && (
                                    <Button size="sm" variant="outline" onClick={() => setEditorTarget(report)}>
                                      <Pencil className="w-3.5 h-3.5 mr-1" />
                                      Edit
                                    </Button>
                                  )}
                                  {report.status === 'draft' && canPublish && (
                                    <Button size="sm" variant="ghost" onClick={() => setPublishTarget(report)}>
                                      <Send className="w-3.5 h-3.5 mr-1" />
                                      Publish
                                    </Button>
                                  )}
                                  {report.status === 'published' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => void download(group, report)}
                                      disabled={downloadingName === report.objectName}
                                    >
                                      <Download className="w-3.5 h-3.5 mr-1" />
                                      {downloadingName === report.objectName ? 'Downloading...' : 'Download PDF'}
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <ServiceReportEditorDialog
        open={editorTarget != null}
        objectName={editorTarget?.objectName || null}
        month={editorTarget?.month || ''}
        onOpenChange={(open) => {
          if (!open) setEditorTarget(null);
        }}
        onSaved={() => void loadReports()}
      />

      <Dialog open={publishTarget != null} onOpenChange={(open) => { if (!open) setPublishTarget(null); }}>
        <DialogContent maxWidth="md">
          <DialogHeader>
            <DialogTitle>Publish {publishTarget?.month || 'report'}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              This copies the draft HTML to the published report. Later edits to the draft do not change the published copy.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPublishTarget(null)}>Cancel</Button>
            <Button onClick={() => void confirmPublish()} disabled={isPublishing}>
              {isPublishing ? 'Publishing...' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ToastContainer toasts={toasts} onRemove={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))} />
    </div>
  );
}
